<!-- src/views/Restaurante/AreasCocinaList.vue -->
<template>
  <div class="p-3 sm:p-6 text-lexend overflow-x-hidden min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-900/80">

    <!-- HEADER PREMIUM -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1.5">
          <div class="w-10 h-1.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-orange-600 dark:text-orange-400 tracking-widest uppercase">Módulo Restaurante</span>
        </div>
        <h1 class="text-2xl sm:text-4xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-3">
          Áreas de Cocina
          <span class="px-3 py-1 bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 text-xs sm:text-sm font-black rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            {{ areas.length }}
          </span>
        </h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">Administra las estaciones de preparación del local</p>
      </div>

      <div class="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
        <div class="hidden sm:flex bg-white dark:bg-gray-800 p-1.5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <button @click="viewMode = 'table'"
            :class="['p-2 rounded-lg transition-all flex items-center',
              viewMode === 'table' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 shadow-sm' : 'text-gray-400 hover:text-gray-600']">
            <Icon icon="mdi:table" class="text-lg" />
          </button>
          <button @click="viewMode = 'cards'"
            :class="['p-2 rounded-lg transition-all flex items-center',
              viewMode === 'cards' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 shadow-sm' : 'text-gray-400 hover:text-gray-600']">
            <Icon icon="mdi:view-grid" class="text-lg" />
          </button>
        </div>

        <button @click="openCreateModal"
          class="w-full sm:w-auto px-4 sm:px-6 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-xl font-black text-xs sm:text-sm tracking-wide transition-all shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2">
          <Icon icon="mdi:plus-circle" class="text-lg" />
          <span>Nueva Área</span>
        </button>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 mb-6 animate-fadeInUp">
      <div class="flex flex-col sm:flex-row gap-4 items-end">
        <div class="flex-1 w-full">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1 uppercase">Buscar Áreas</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-orange-500 transition-colors" />
            <input v-model="searchQuery" type="text" placeholder="Buscar por nombre, descripción o impresora..."
              class="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-transparent focus:border-orange-500 focus:bg-white dark:focus:bg-gray-800 rounded-xl transition-all outline-none dark:text-white font-bold text-sm" />
          </div>
        </div>

        <div class="w-full sm:w-48">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1 uppercase">Estado</label>
          <select v-model="filterStatus"
            class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-transparent focus:border-orange-500 rounded-xl font-bold text-gray-700 dark:text-gray-200 outline-none transition-all cursor-pointer text-sm appearance-none">
            <option value="all">Todos los estados</option>
            <option value="activos">Solo Activas</option>
            <option value="inactivos">Solo Inactivas</option>
          </select>
        </div>

        <button @click="cargarAreas"
          class="w-full sm:w-auto px-5 py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2">
          <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" class="text-lg" />
          <span class="hidden sm:inline">Actualizar</span>
        </button>
      </div>
    </div>

    <!-- ESTADÍSTICAS -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-6 animate-fadeInUp delay-100">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-orange-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Total Áreas</p>
            <p class="text-3xl font-black text-gray-900 dark:text-white">{{ areas.length }}</p>
          </div>
          <div class="p-2.5 bg-orange-50 dark:bg-orange-900/30 rounded-xl">
            <Icon icon="mdi:stove" class="text-orange-600 dark:text-orange-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10">
          <span class="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-[10px] font-black">{{ areasFiltradas.length }} filtradas</span>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Activas</p>
            <p class="text-3xl font-black text-emerald-600">{{ estadisticas.activas }}</p>
          </div>
          <div class="p-2.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl">
            <Icon icon="mdi:check-decagram" class="text-emerald-600 dark:text-emerald-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10">
          <div class="w-full bg-gray-100 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
            <div class="bg-emerald-500 h-full rounded-full transition-all duration-500" :style="{ width: estadisticas.porcentajeActivas + '%' }"></div>
          </div>
          <span class="text-[10px] font-black text-emerald-600 mt-1.5 block">{{ estadisticas.porcentajeActivas }}% del total</span>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-rose-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Inactivas</p>
            <p class="text-3xl font-black text-rose-600">{{ estadisticas.inactivas }}</p>
          </div>
          <div class="p-2.5 bg-rose-50 dark:bg-rose-900/30 rounded-xl">
            <Icon icon="mdi:close-circle" class="text-rose-600 dark:text-rose-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10 flex items-center text-[10px] font-black text-rose-500">
          <Icon icon="mdi:alert-circle" class="mr-1 text-sm" /> Áreas desactivadas
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-indigo-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Con Impresora</p>
            <p class="text-3xl font-black text-indigo-600">{{ estadisticas.conImpresora }}</p>
          </div>
          <div class="p-2.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl">
            <Icon icon="mdi:printer" class="text-indigo-600 dark:text-indigo-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10 flex items-center text-[10px] font-black text-indigo-500">
          <Icon icon="mdi:printer-check" class="mr-1 text-sm" /> Configuradas
        </div>
      </div>
    </div>

    <!-- CONTENIDO -->
    <div class="animate-fadeInUp delay-200">
      <!-- VISTA TABLA -->
      <div v-if="viewMode === 'table'" class="hidden lg:block bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div class="overflow-x-auto custom-scrollbar">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50/80 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase text-center">#</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase">Área</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase">Descripción</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase">Impresora</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase text-center">Estado</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase">Creación</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr v-if="loading" v-for="i in 3" :key="'load-'+i" class="animate-pulse">
                <td colspan="7" class="px-6 py-5"><div class="h-5 bg-gray-100 dark:bg-gray-700 rounded-lg w-full"></div></td>
              </tr>
              <tr v-if="!loading && areasFiltradas.length === 0">
                <td colspan="7" class="px-6 py-20 text-center">
                  <div class="flex flex-col items-center opacity-40 select-none">
                    <Icon icon="mdi:stove-off" class="text-8xl mb-4 text-gray-300 dark:text-gray-600" />
                    <h4 class="text-xl font-black text-gray-400 tracking-widest uppercase">No hay áreas</h4>
                    <p class="text-sm text-gray-400 mt-1 font-medium">Registra tu primera área de cocina</p>
                  </div>
                </td>
              </tr>
              <tr v-for="area in areasFiltradas" :key="area.idAreaCocina"
                class="hover:bg-orange-50/40 dark:hover:bg-gray-700/40 transition-all duration-200 group">
                <td class="px-6 py-4 text-center">
                  <span class="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 font-black text-sm border border-orange-100 dark:border-orange-800/50">
                    {{ area.orden }}
                  </span>
                </td>
                <td class="px-6 py-4">
                  <div class="flex items-center gap-3">
                    <div :class="['h-11 w-11 rounded-xl flex items-center justify-center shadow-sm border',
                      area.estado === 'activo'
                        ? 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-100 dark:from-orange-900/40 dark:to-red-900/40 dark:border-orange-800/50'
                        : 'bg-gray-50 border-gray-100 dark:bg-gray-800 dark:border-gray-700']">
                      <Icon icon="mdi:stove"
                        :class="['text-xl', area.estado === 'activo' ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400']" />
                    </div>
                    <div>
                      <div class="text-sm font-black text-gray-900 dark:text-white">{{ area.nombre }}</div>
                      <div class="text-[11px] font-bold text-gray-400">ID: {{ area.idAreaCocina }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <span class="text-sm font-medium text-gray-600 dark:text-gray-300 truncate max-w-[250px] block">
                    {{ area.descripcion || 'Sin descripción' }}
                  </span>
                </td>
                <td class="px-6 py-4">
                  <span v-if="area.impresora"
                    class="px-3 py-1.5 inline-flex items-center text-[11px] font-black uppercase tracking-widest rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800/50">
                    <Icon icon="mdi:printer" class="mr-1.5 text-sm" /> {{ area.impresora }}
                  </span>
                  <span v-else class="text-xs text-gray-400 italic">No configurada</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <span :class="['px-3 py-1.5 inline-flex items-center text-[11px] font-black uppercase tracking-widest rounded-lg border',
                    area.estado === 'activo'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50'
                      : 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/50']">
                    <Icon :icon="area.estado === 'activo' ? 'mdi:check-circle' : 'mdi:close-circle'" class="mr-1.5 text-sm" />
                    {{ convertirEstadoATexto(area.estado) }}
                  </span>
                </td>
                <td class="px-6 py-4">
                  <span class="text-xs font-bold text-gray-500 dark:text-gray-400">{{ formatearFechaArea(area.fecha_creacion) }}</span>
                </td>
                <td class="px-6 py-4 text-right">
                  <div class="flex items-center justify-end gap-2">
                    <button @click.stop="openEditModal(area)" title="Editar"
                      class="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-amber-500 rounded-lg hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 transition-all shadow-sm">
                      <Icon icon="mdi:pencil" class="text-lg" />
                    </button>
                    <button @click.stop="abrirModalToggleStatus(area)" :title="area.estado === 'activo' ? 'Desactivar' : 'Activar'"
                      class="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-indigo-500 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition-all shadow-sm">
                      <Icon :icon="area.estado === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-lg" />
                    </button>
                    <button @click.stop="abrirModalEliminar(area)" title="Eliminar"
                      class="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-rose-500 rounded-lg hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-all shadow-sm">
                      <Icon icon="mdi:delete" class="text-lg" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- VISTA TARJETAS -->
      <div v-if="viewMode === 'cards' || windowWidth < 1024"
        class="lg:grid lg:grid-cols-2 lg:gap-4 xl:grid-cols-3 space-y-4 lg:space-y-0">
        <div v-if="loading" v-for="i in 3" :key="'load-mobile-'+i"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 animate-pulse h-40"></div>
        <div v-if="!loading && areasFiltradas.length === 0"
          class="col-span-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-10 text-center">
          <div class="flex flex-col items-center opacity-40 select-none">
            <Icon icon="mdi:stove-off" class="text-6xl mb-3 text-gray-300 dark:text-gray-600" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest uppercase">No hay áreas</h4>
            <p class="text-sm text-gray-400 mt-1">Crea una nueva área para empezar</p>
          </div>
        </div>

        <div v-for="area in areasFiltradas" :key="'card-'+area.idAreaCocina"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 group flex flex-col">
          <div class="p-5 flex-1">
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center gap-3">
                <div :class="['w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border',
                  area.estado === 'activo'
                    ? 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-100 dark:from-orange-900/40 dark:to-red-900/40 dark:border-orange-800/50'
                    : 'bg-gray-50 border-gray-100 dark:bg-gray-800 dark:border-gray-700']">
                  <Icon icon="mdi:stove"
                    :class="['text-2xl', area.estado === 'activo' ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400']" />
                </div>
                <div>
                  <h3 class="font-black text-gray-900 dark:text-white text-base">{{ area.nombre }}</h3>
                  <p class="text-xs font-bold text-gray-400 mt-0.5">Orden #{{ area.orden }} · ID: {{ area.idAreaCocina }}</p>
                </div>
              </div>
              <span :class="['px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border',
                area.estado === 'activo'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50'
                  : 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/50']">
                {{ convertirEstadoATexto(area.estado) }}
              </span>
            </div>

            <p class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">
              {{ area.descripcion || 'Sin descripción' }}
            </p>

            <div class="grid grid-cols-2 gap-3 mb-2">
              <div class="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 border border-gray-100 dark:border-gray-700/50">
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Impresora</p>
                <p class="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 mt-1 truncate">
                  {{ area.impresora || 'No configurada' }}
                </p>
              </div>
              <div class="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 border border-gray-100 dark:border-gray-700/50">
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Creada</p>
                <p class="text-[11px] font-bold text-gray-700 dark:text-gray-300 mt-1">{{ formatearFechaArea(area.fecha_creacion) }}</p>
              </div>
            </div>
          </div>

          <div class="bg-gray-50/50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700 p-3 grid grid-cols-3 gap-2">
            <button @click.stop="openEditModal(area)"
              class="py-2.5 bg-white dark:bg-gray-800 text-amber-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-amber-50 transition-colors">
              <Icon icon="mdi:pencil" class="text-base sm:text-sm" /><span>Editar</span>
            </button>
            <button @click.stop="abrirModalToggleStatus(area)"
              class="py-2.5 bg-white dark:bg-gray-800 text-indigo-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-indigo-50 transition-colors">
              <Icon :icon="area.estado === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-base sm:text-sm" />
              <span>{{ area.estado === 'activo' ? 'Desactivar' : 'Activar' }}</span>
            </button>
            <button @click.stop="abrirModalEliminar(area)"
              class="py-2.5 bg-white dark:bg-gray-800 text-rose-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-rose-50 transition-colors">
              <Icon icon="mdi:delete" class="text-base sm:text-sm" /><span>Eliminar</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ MODAL FORMULARIO ============ -->
    <Teleport to="body">
      <div v-if="showModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="closeModal">
        <div class="bg-white dark:bg-gray-800 w-full max-w-lg sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn flex flex-col max-h-[90vh]">
          <!-- Header gradiente -->
          <div class="bg-gradient-to-r from-orange-600 to-red-600 px-5 py-4 flex items-center gap-3 shrink-0">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editingArea ? 'mdi:pencil-box' : 'mdi:stove'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-base font-black text-white">{{ editingArea ? 'Editar Área' : 'Nueva Área de Cocina' }}</h3>
              <p class="text-[11px] text-white/80 font-bold">{{ editingArea ? 'Modifica los datos' : 'Completa los datos' }}</p>
            </div>
            <button @click="closeModal" class="ml-auto p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>

          <!-- Body con scroll -->
          <div class="p-5 overflow-y-auto custom-scrollbar">
            <form @submit.prevent="saveArea" class="space-y-4">

              <!-- Nombre -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Nombre del Área *</label>
                <input v-model="form.nombre" type="text" required maxlength="100"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-orange-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all"
                  placeholder="Ej: Cocina Caliente, Parrilla, Barra de Bebidas" />
              </div>

              <!-- Orden + Impresora -->
              <div class="grid grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Orden</label>
                  <input v-model.number="form.orden" type="number" min="0" max="999"
                    class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-orange-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all" />
                  <p class="text-[9px] font-medium text-gray-400 mt-1">Menor número = primero</p>
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Impresora</label>
                  <input v-model="form.impresora" type="text" maxlength="100"
                    class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-orange-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all"
                    placeholder="Ej: IMP-COCINA-01" />
                </div>
              </div>

              <!-- Descripción -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Descripción</label>
                <textarea v-model="form.descripcion" rows="2" maxlength="200"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-orange-500 rounded-xl text-sm font-medium text-gray-800 dark:text-white outline-none transition-all resize-none"
                  placeholder="Descripción opcional..."></textarea>
              </div>

              <!-- Estado -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-3 uppercase">Estado</label>
                <div class="grid grid-cols-2 gap-3">
                  <label :class="['flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-black text-xs cursor-pointer transition-all',
                    form.estado === 'activo'
                      ? 'bg-emerald-50 border-emerald-500 text-emerald-700 dark:bg-emerald-900/30'
                      : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-800 dark:border-gray-700']">
                    <input type="radio" v-model="form.estado" value="activo" class="hidden" />
                    <Icon icon="mdi:check-circle" class="text-base" /> Activa
                  </label>
                  <label :class="['flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-black text-xs cursor-pointer transition-all',
                    form.estado === 'inactivo'
                      ? 'bg-rose-50 border-rose-500 text-rose-700 dark:bg-rose-900/30'
                      : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-800 dark:border-gray-700']">
                    <input type="radio" v-model="form.estado" value="inactivo" class="hidden" />
                    <Icon icon="mdi:close-circle" class="text-base" /> Inactiva
                  </label>
                </div>
              </div>

              <!-- Botones -->
              <div class="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button type="button" @click="closeModal"
                  class="w-full sm:w-auto px-6 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all">
                  Cancelar
                </button>
                <button type="submit" :disabled="saving"
                  class="w-full flex-1 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-orange-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': saving}" class="text-lg" />
                  {{ saving ? 'Guardando...' : (editingArea ? 'Actualizar Área' : 'Crear Área') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============ MODAL CONFIRMACIÓN ============ -->
    <Teleport to="body">
      <div v-if="showConfirmModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10002] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 w-full max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn">
          <div class="px-5 py-4" :class="confirmModalColor">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon :icon="confirmModalIcon" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-base font-black text-white">{{ confirmModalTitulo }}</h3>
                <p class="text-[11px] text-white/80 font-bold">{{ confirmModalSubtitulo }}</p>
              </div>
            </div>
          </div>
          <div class="p-6 text-center">
            <p class="text-sm font-bold text-gray-700 dark:text-gray-300">{{ confirmModalMensaje }}</p>
            <p v-if="confirmModalDetalle" class="text-xs text-gray-500 dark:text-gray-400 mt-2">{{ confirmModalDetalle }}</p>
          </div>
          <div class="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 flex flex-col-reverse sm:flex-row gap-3">
            <button @click="cancelarConfirmacion"
              class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-100 transition-all">
              Cancelar
            </button>
            <button @click="ejecutarAccionConfirmada" :disabled="confirmModalCargando"
              class="w-full sm:flex-[2] py-3 rounded-xl font-black text-xs uppercase tracking-widest text-white hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg"
              :class="confirmModalButtonColor">
              <Icon :icon="confirmModalCargando ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': confirmModalCargando}" class="text-lg" />
              {{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import Swal from 'sweetalert2';
import {
  obtenerAreas, crearArea, actualizarArea, eliminarArea, activarArea, desactivarArea,
  formatearFechaArea, convertirEstadoATexto, obtenerEstadisticasAreas, buscarAreas, filtrarAreasPorEstado,
} from '@/api/areasCocina.js';

export default {
  name: 'AreasCocinaList',
  components: { Icon },
  data() {
    return {
      areas: [], loading: false, saving: false, showModal: false,
      searchQuery: '', filterStatus: 'all',
      viewMode: 'table', windowWidth: window.innerWidth,
      form: { nombre: '', descripcion: '', impresora: '', estado: 'activo', orden: 0 },
      editingArea: null,

      // Modal confirmación
      showConfirmModal: false, confirmModalTitulo: '', confirmModalSubtitulo: '',
      confirmModalMensaje: '', confirmModalDetalle: '', confirmModalIcon: '',
      confirmModalColor: '', confirmModalButtonColor: '', confirmModalBotonTexto: '',
      confirmModalCargando: false, confirmModalAccion: null, confirmModalDatos: null,
    };
  },
  computed: {
    areasFiltradas() {
      let filtered = [...this.areas];
      if (this.searchQuery.trim()) filtered = buscarAreas(filtered, this.searchQuery);
      filtered = filtrarAreasPorEstado(filtered, this.filterStatus);
      // Siempre ordenadas por campo `orden`
      return filtered.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    },
    estadisticas() { return obtenerEstadisticasAreas(this.areas); },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.cargarAreas();
  },
  beforeUnmount() { window.removeEventListener('resize', this.handleResize); },
  methods: {
    handleResize() { this.windowWidth = window.innerWidth; },

    mostrarToast(icon, title, text = '') {
      Swal.fire({ toast: true, position: 'top-end', icon, title, text, showConfirmButton: false, timer: 3000, timerProgressBar: true });
    },

    async cargarAreas() {
      this.loading = true;
      try {
        const r = await obtenerAreas();
        if (r.success) this.areas = r.data || [];
      } finally { this.loading = false; }
    },

    openCreateModal() {
      this.editingArea = null;
      // Sugerir el siguiente `orden` disponible
      const maxOrden = this.areas.reduce((m, a) => Math.max(m, a.orden ?? 0), 0);
      this.form = { nombre: '', descripcion: '', impresora: '', estado: 'activo', orden: maxOrden + 1 };
      this.showModal = true;
    },
    openEditModal(area) {
      this.editingArea = area;
      this.form = {
        nombre: area.nombre, descripcion: area.descripcion || '',
        impresora: area.impresora || '', estado: area.estado,
        orden: area.orden ?? 0,
      };
      this.showModal = true;
    },
    closeModal() { this.showModal = false; },

    async saveArea() {
      this.saving = true;
      try {
        let result;
        if (this.editingArea) result = await actualizarArea(this.editingArea.idAreaCocina, this.form);
        else result = await crearArea(this.form);

        if (result.success) {
          await this.cargarAreas();
          this.closeModal();
          this.mostrarToast('success', '¡Éxito!', `El área se ha ${this.editingArea ? 'actualizado' : 'creado'} correctamente.`);
        } else {
          this.mostrarToast('error', 'Error al guardar', result.error || 'Ocurrió un error al guardar el área.');
        }
      } catch (error) {
        this.mostrarToast('error', 'Error inesperado', error.message || 'Ocurrió un error al guardar.');
      } finally { this.saving = false; }
    },

    abrirModalEliminar(area) {
      this.abrirModalConfirmacion({
        titulo: 'Eliminar Área', subtitulo: 'Esta acción es permanente',
        mensaje: `¿Eliminar el área "${area.nombre}"?`,
        detalle: 'El área se marcará como inactiva. Esta acción no se puede deshacer.',
        icon: 'mdi:alert-circle',
        color: 'bg-gradient-to-r from-red-600 to-pink-600',
        buttonColor: 'bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700',
        botonTexto: 'Eliminar', datos: area.idAreaCocina,
        accion: (id) => this.deleteArea(id),
      });
    },
    async deleteArea(id) {
      try {
        const r = await eliminarArea(id);
        if (r.success) {
          await this.cargarAreas();
          this.mostrarToast('success', '¡Eliminada!', 'El área ha sido desactivada correctamente.');
        } else {
          this.mostrarToast('error', 'No se pudo eliminar', r.error || 'No se puede eliminar el área.');
        }
      } catch (error) {
        this.mostrarToast('error', 'Error inesperado', error.message || 'Ocurrió un error al eliminar.');
      }
    },

    abrirModalToggleStatus(area) {
      const esActiva = area.estado === 'activo';
      this.abrirModalConfirmacion({
        titulo: esActiva ? 'Desactivar Área' : 'Activar Área',
        subtitulo: 'Cambiar estado del área',
        mensaje: `¿${esActiva ? 'desactivar' : 'activar'} "${area.nombre}"?`,
        detalle: `Pasará de "${convertirEstadoATexto(area.estado)}" a "${convertirEstadoATexto(esActiva ? 'inactivo' : 'activo')}".`,
        icon: esActiva ? 'mdi:close-circle' : 'mdi:check-circle',
        color: esActiva
          ? 'bg-gradient-to-r from-orange-500 to-amber-600'
          : 'bg-gradient-to-r from-green-600 to-emerald-600',
        buttonColor: esActiva
          ? 'bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700'
          : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700',
        botonTexto: esActiva ? 'Desactivar' : 'Activar',
        datos: area, accion: (a) => this.toggleStatus(a),
      });
    },
    async toggleStatus(area) {
      const esActiva = area.estado === 'activo';
      try {
        let r;
        if (esActiva) r = await desactivarArea(area.idAreaCocina);
        else r = await activarArea(area.idAreaCocina);

        if (r.success) {
          await this.cargarAreas();
          this.mostrarToast('success', '¡Actualizada!', `El área ha sido ${esActiva ? 'desactivada' : 'activada'} correctamente.`);
        } else {
          this.mostrarToast('error', 'Error al actualizar', r.error || 'Ocurrió un error al cambiar el estado.');
        }
      } catch (error) {
        this.mostrarToast('error', 'Error inesperado', error.message || 'Ocurrió un error al cambiar el estado.');
      }
    },

    // ========== MODAL CONFIRMACIÓN ==========
    abrirModalConfirmacion(config) {
      this.confirmModalTitulo = config.titulo || 'Confirmar';
      this.confirmModalSubtitulo = config.subtitulo || '';
      this.confirmModalMensaje = config.mensaje || '¿Estás seguro?';
      this.confirmModalDetalle = config.detalle || '';
      this.confirmModalIcon = config.icon || 'mdi:help-circle';
      this.confirmModalColor = config.color || 'bg-gradient-to-r from-blue-600 to-indigo-600';
      this.confirmModalButtonColor = config.buttonColor || 'bg-gradient-to-r from-blue-600 to-indigo-600';
      this.confirmModalBotonTexto = config.botonTexto || 'Confirmar';
      this.confirmModalAccion = config.accion;
      this.confirmModalDatos = config.datos || null;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalCargando = false;
      this.confirmModalAccion = null;
    },
    async ejecutarAccionConfirmada() {
      if (this.confirmModalAccion) {
        this.confirmModalCargando = true;
        await this.confirmModalAccion(this.confirmModalDatos);
        this.confirmModalCargando = false;
        this.cancelarConfirmacion();
      }
    },

    formatearFechaArea,
    convertirEstadoATexto,
  },
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; }

@keyframes zoomIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
.animate-zoomIn { animation: zoomIn 0.25s cubic-bezier(0.16, 1, 0.3, 1); }

@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.animate-slideUp { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }

@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.delay-100 { animation-delay: 0.1s; }
.delay-200 { animation-delay: 0.2s; }

@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }

@media (max-width: 768px) {
  button { min-height: 44px; }
  input, select, textarea { font-size: 16px !important; }
}
</style>